import { useState, useEffect, useRef } from 'react';
import { supabase } from '../../../lib/supabase';

interface Props {
  clientId: string;
  clientName?: string;
  initialDutyAmount?: number | null;
  initialDutyPaymentDate?: string | null;
  initialTariffCodes?: string | null;
  readOnly?: boolean;
  /** Admin mode: shows all fields + TOTAL ESTIMATED TARIFF REFUND editable field */
  adminMode?: boolean;
  /** When true (eligibility authorized by attorney), client sees ONLY the green TOTAL ESTIMATED TARIFF REFUND field */
  eligibilityAuthorized?: boolean;
}

// ── Tooltip component ──────────────────────────────────────────────────────
function InfoTooltip({ content }: { content: string }) {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setVisible(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div className="relative inline-flex items-center" ref={ref}>
      <button
        type="button"
        onMouseEnter={() => setVisible(true)}
        onMouseLeave={() => setVisible(false)}
        onClick={() => setVisible(v => !v)}
        className="w-4 h-4 flex items-center justify-center bg-[#FDB913]/20 border border-[#FDB913]/40 rounded-full cursor-pointer ml-1 flex-shrink-0"
        aria-label="More information"
      >
        <i className="ri-question-line text-[#FDB913] text-[9px]"></i>
      </button>
      {visible && (
        <div className="absolute left-6 top-0 z-50 w-80 bg-[#0d1520] border border-[#FDB913]/40 rounded-xl p-3 shadow-2xl">
          <p className="text-white/80 text-xs leading-relaxed">{content}</p>
          <div className="absolute -left-1.5 top-2 w-3 h-3 bg-[#0d1520] border-l border-t border-[#FDB913]/40 rotate-[-45deg]"></div>
        </div>
      )}
    </div>
  );
}

const LIQUIDATION_FINAL_TOOLTIP = `A duty entry is considered "final liquidated" when U.S. Customs and Border Protection (CBP) has completed the assessment of duties, taxes, and fees on a specific import entry, and the statutory period for CBP to reliquidate that entry (or for an importer to protest the decision) has expired without further action.

Under 19 U.S.C. § 1514, the liquidation becomes "final and conclusive" upon the expiration of these time limits, barring fraud or gross negligence.

The Statutory Limit (365 Days): Under 19 U.S.C. § 1504, CBP has one year (365 days) from the date of entry (or withdrawal from warehouse) to formally liquidate an entry. If CBP does not act within this 365-day window, the entry is "deemed liquidated" at the rate of duty stated in the entry summary — an automatic legal event that happens exactly on day 366.

If no protest is filed within 90 days of notice of liquidation, the decision becomes final. The timeframe for liquidation begins from the date of entry (or the date of withdrawal from warehouse), not the date of deposit or payment.`;

export default function TariffRefundEstimator({
  clientId, clientName,
  initialDutyAmount, initialDutyPaymentDate, initialTariffCodes,
  readOnly = false,
  adminMode = false,
  eligibilityAuthorized = false,
}: Props) {
  const [dutyAmount, setDutyAmount] = useState(initialDutyAmount?.toString() || '');
  const [dutyPaymentDate, setDutyPaymentDate] = useState(initialDutyPaymentDate || '');
  const [tariffCodes, setTariffCodes] = useState(initialTariffCodes || '');
  const [liquidationStatus, setLiquidationStatus] = useState<'unliquidated' | 'liquidation_final' | ''>('');
  const [liquidationDate, setLiquidationDate] = useState('');
  const [protestFileDate, setProtestFileDate] = useState('');
  const [totalEstimatedRefund, setTotalEstimatedRefund] = useState('');
  const [loading, setLoading] = useState(!initialDutyAmount && !initialDutyPaymentDate);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (initialDutyAmount !== undefined || initialDutyPaymentDate !== undefined) {
      setLoading(false);
      return;
    }
    const loadData = async () => {
      try {
        const { data } = await supabase.from('clients')
          .select('duty_amount_paid, duty_payment_date, tariff_codes, liquidation_status, liquidation_date, protest_file_date, total_estimated_tariff_refund')
          .eq('id', clientId).maybeSingle();
        if (data) {
          const d = data as Record<string, unknown>;
          setDutyAmount(d.duty_amount_paid?.toString() || '');
          setDutyPaymentDate((d.duty_payment_date as string) || '');
          setTariffCodes((d.tariff_codes as string) || '');
          setLiquidationStatus((d.liquidation_status as 'unliquidated' | 'liquidation_final' | '') || '');
          setLiquidationDate((d.liquidation_date as string) || '');
          setProtestFileDate((d.protest_file_date as string) || '');
          setTotalEstimatedRefund(d.total_estimated_tariff_refund?.toString() || '');
        }
      } catch {
        // ignore
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [clientId, initialDutyAmount, initialDutyPaymentDate]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const updatePayload: Record<string, unknown> = {
        duty_amount_paid: dutyAmount ? parseFloat(dutyAmount) : null,
        duty_payment_date: dutyPaymentDate || null,
        tariff_codes: tariffCodes || null,
        liquidation_status: liquidationStatus || null,
        liquidation_date: liquidationDate || null,
        protest_file_date: protestFileDate || null,
      };
      if (adminMode) {
        updatePayload.total_estimated_tariff_refund = totalEstimatedRefund ? parseFloat(totalEstimatedRefund) : null;
      }
      await supabase.from('clients').update(updatePayload).eq('id', clientId);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      console.error('Save error:', err);
    } finally {
      setSaving(false);
    }
  };

  const parsedAmount = parseFloat(dutyAmount) || 0;
  const hasData = parsedAmount > 0 && dutyPaymentDate;

  // Protest deadline warning (within 30 days)
  const protestWarning = (() => {
    if (!protestFileDate) return null;
    const daysLeft = Math.ceil((new Date(protestFileDate).getTime() - Date.now()) / 86400000);
    if (daysLeft <= 30 && daysLeft >= 0) return daysLeft;
    return null;
  })();

  if (loading) return null;

  return (
    <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#FDB913]/15 to-[#FDB913]/5 border-b border-[#FDB913]/20 p-6">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 flex items-center justify-center bg-[#FDB913]/20 rounded-xl border border-[#FDB913]/40 flex-shrink-0">
            <i className="ri-money-dollar-circle-fill text-[#FDB913] text-2xl"></i>
          </div>
          <div>
            <h3 className="text-xl font-black text-white">Estimated Tariff Refund</h3>
          </div>
        </div>
      </div>

      <div className="p-6 space-y-6">
        {/* ── Eligibility authorized: client sees ONLY the green total field ── */}
        {!adminMode && eligibilityAuthorized && (
          <div className="bg-gradient-to-br from-green-500/10 to-green-500/5 border-2 border-green-500/30 rounded-2xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center bg-green-500/20 rounded-xl border border-green-500/30 flex-shrink-0">
                <i className="ri-award-fill text-green-400 text-xl"></i>
              </div>
              <div>
                <p className="text-white/50 text-xs font-black uppercase tracking-wider">Total Estimated Tariff Refund</p>
                <p className="text-white/30 text-xs mt-0.5">Calculated and confirmed by your attorney</p>
              </div>
            </div>
            {totalEstimatedRefund && parseFloat(totalEstimatedRefund) > 0 ? (
              <p className="text-5xl font-black text-green-400">
                ${parseFloat(totalEstimatedRefund).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
            ) : (
              <p className="text-white/25 text-sm italic">Pending attorney review — your attorney will update this field once your case is assessed.</p>
            )}
          </div>
        )}

        {/* ── All other content hidden when eligibilityAuthorized (client mode) ── */}
        {(adminMode || !eligibilityAuthorized) && <>

        {/* Protest deadline warning */}
        {protestWarning !== null && (
          <div className={`flex items-start gap-3 p-4 rounded-xl border-2 ${protestWarning <= 7 ? 'bg-red-500/15 border-red-500/50' : 'bg-yellow-500/10 border-yellow-500/40'}`}>
            <div className={`w-9 h-9 flex items-center justify-center rounded-lg flex-shrink-0 ${protestWarning <= 7 ? 'bg-red-500/20' : 'bg-yellow-500/15'}`}>
              <i className={`ri-alarm-warning-fill text-lg ${protestWarning <= 7 ? 'text-red-400' : 'text-yellow-400'}`}></i>
            </div>
            <div>
              <p className={`font-black text-sm ${protestWarning <= 7 ? 'text-red-400' : 'text-yellow-400'}`}>
                {protestWarning === 0 ? 'Protest File Date is TODAY!' : `Protest File Date in ${protestWarning} day${protestWarning !== 1 ? 's' : ''}!`}
              </p>
              <p className="text-white/60 text-xs mt-0.5">
                Your protest must be filed by {new Date(protestFileDate).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}. Contact your attorney immediately.
              </p>
            </div>
          </div>
        )}

        {/* Refund amount display */}
        {hasData ? (
          <div className="bg-gradient-to-br from-[#FDB913]/20 to-[#FDB913]/10 border-2 border-[#FDB913]/40 rounded-2xl p-6 text-center">
            <p className="text-white/60 text-sm mb-2">Estimated Refund Amount</p>
            <p className="text-5xl font-black text-[#FDB913]">
              ${parsedAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </p>
            <p className="text-white/40 text-xs mt-2 leading-relaxed">
              Refunds are calculated on actual cash paid per the Entry Summary (CBP Form 7501).<br />
              Bond/penalty fees may also qualify — consult your attorney.
            </p>
            {liquidationStatus && (
              <div className={`mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-black ${
                liquidationStatus === 'liquidation_final'
                  ? 'bg-red-500/15 border-red-500/30 text-red-400'
                  : 'bg-yellow-500/10 border-yellow-500/30 text-yellow-400'
              }`}>
                <i className={liquidationStatus === 'liquidation_final' ? 'ri-lock-fill' : 'ri-time-line'}></i>
                {liquidationStatus === 'liquidation_final' ? 'Liquidation Final' : 'Unliquidated'}
              </div>
            )}
            {tariffCodes && (
              <div className="mt-3 flex flex-wrap items-center justify-center gap-1.5">
                <span className="text-white/30 text-xs">Tariff codes:</span>
                {tariffCodes.split(/[,;\s]+/).filter(Boolean).map((code, i) => (
                  <span key={i} className="px-2 py-0.5 bg-[#FDB913]/10 border border-[#FDB913]/20 text-[#FDB913] text-xs font-bold rounded-full">
                    {code}
                  </span>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="bg-white/5 border border-dashed border-white/15 rounded-2xl p-8 text-center">
            <div className="w-14 h-14 flex items-center justify-center bg-[#FDB913]/10 border border-[#FDB913]/20 rounded-full mx-auto mb-3">
              <i className="ri-money-dollar-circle-line text-[#FDB913]/40 text-2xl"></i>
            </div>
            <p className="text-white/40 text-sm">
              Duty amount and payment date not yet on file.
            </p>
          </div>
        )}

        {/* TOTAL ESTIMATED TARIFF REFUND — client read-only display */}
        {!adminMode && (
          <div className="bg-gradient-to-br from-green-500/10 to-green-500/5 border-2 border-green-500/30 rounded-2xl p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-9 h-9 flex items-center justify-center bg-green-500/20 rounded-xl border border-green-500/30 flex-shrink-0">
                <i className="ri-award-fill text-green-400 text-lg"></i>
              </div>
              <div>
                <p className="text-white/50 text-xs font-black uppercase tracking-wider">Total Estimated Tariff Refund</p>
                <p className="text-white/30 text-xs mt-0.5">Calculated and confirmed by your attorney</p>
              </div>
            </div>
            {totalEstimatedRefund && parseFloat(totalEstimatedRefund) > 0 ? (
              <p className="text-4xl font-black text-green-400">
                ${parseFloat(totalEstimatedRefund).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
            ) : (
              <p className="text-white/25 text-sm italic">Pending attorney review — your attorney will update this field once your case is assessed.</p>
            )}
          </div>
        )}

        {/* Input fields — editable if not readOnly AND in adminMode */}
        {!readOnly && adminMode && (
          <div className="space-y-4">
            <p className="text-white/50 text-xs font-black uppercase tracking-wider">
              <i className="ri-edit-line mr-1"></i>Update Information
            </p>

            <div>
              <label className="block text-xs font-bold text-white/60 uppercase tracking-wider mb-2">
                Amount Paid — Exact Duty Amount (from CBP Form 7501) <span className="text-red-400">*</span>
              </label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40 text-sm">$</span>
                <input
                  type="number" step="0.01" min="0"
                  value={dutyAmount} onChange={e => setDutyAmount(e.target.value)}
                  className="w-full pl-8 pr-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/30 focus:outline-none focus:border-[#FDB913] text-sm transition-colors"
                  placeholder="0.00"
                />
              </div>
              <p className="text-white/30 text-xs mt-1">
                Enter the exact cash duty amount shown on your Entry Summary. Bond/penalty fees may be added separately.
              </p>
            </div>

            <div>
              <label className="block text-xs font-bold text-white/60 uppercase tracking-wider mb-2">
                Date Duty Was Paid <span className="text-red-400">*</span>
              </label>
              <input
                type="date" value={dutyPaymentDate} onChange={e => setDutyPaymentDate(e.target.value)}
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#FDB913] text-sm transition-colors"
              />
              <p className="text-white/30 text-xs mt-1">The 3-year filing deadline is calculated from this date.</p>
            </div>

            <div>
              <label className="block text-xs font-bold text-white/60 uppercase tracking-wider mb-2">
                TARIFF CODES/ENTRIES FROM SUMMARY WHERE IEEPA DUTIES WERE DEPOSITED/PAID
              </label>
              <input
                type="text" value={tariffCodes} onChange={e => setTariffCodes(e.target.value)} maxLength={500}
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/30 focus:outline-none focus:border-[#FDB913] text-sm transition-colors"
                placeholder="e.g. 9903.88.01, 7308.90.9590 — Entry #XXXX-XXXXXXX-X"
              />
            </div>

            {/* Liquidation Status */}
            <div>
              <label className="block text-xs font-bold text-white/60 uppercase tracking-wider mb-2">
                Liquidation Status
              </label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setLiquidationStatus(liquidationStatus === 'unliquidated' ? '' : 'unliquidated')}
                  className={`flex items-center gap-2.5 p-3 rounded-xl border-2 text-left transition-all cursor-pointer ${
                    liquidationStatus === 'unliquidated'
                      ? 'border-yellow-500/60 bg-yellow-500/10'
                      : 'border-white/10 bg-white/5 hover:border-white/20'
                  }`}
                >
                  <div className={`w-5 h-5 flex-shrink-0 border-2 flex items-center justify-center rounded ${
                    liquidationStatus === 'unliquidated' ? 'border-yellow-500 bg-yellow-500' : 'border-white/30 bg-white/5'
                  }`}>
                    {liquidationStatus === 'unliquidated' && <i className="ri-check-line text-black text-xs"></i>}
                  </div>
                  <div>
                    <p className={`text-xs font-black ${liquidationStatus === 'unliquidated' ? 'text-yellow-400' : 'text-white/50'}`}>UNLIQUIDATED</p>
                    <p className="text-white/30 text-[10px]">Entry not yet liquidated by CBP</p>
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => setLiquidationStatus(liquidationStatus === 'liquidation_final' ? '' : 'liquidation_final')}
                  className={`flex items-center gap-2.5 p-3 rounded-xl border-2 text-left transition-all cursor-pointer ${
                    liquidationStatus === 'liquidation_final'
                      ? 'border-red-500/60 bg-red-500/10'
                      : 'border-white/10 bg-white/5 hover:border-white/20'
                  }`}
                >
                  <div className={`w-5 h-5 flex-shrink-0 border-2 flex items-center justify-center rounded ${
                    liquidationStatus === 'liquidation_final' ? 'border-red-500 bg-red-500' : 'border-white/30 bg-white/5'
                  }`}>
                    {liquidationStatus === 'liquidation_final' && <i className="ri-check-line text-white text-xs"></i>}
                  </div>
                  <div className="flex items-start gap-1">
                    <div>
                      <p className={`text-xs font-black ${liquidationStatus === 'liquidation_final' ? 'text-red-400' : 'text-white/50'}`}>LIQUIDATION FINAL</p>
                      <p className="text-white/30 text-[10px]">CBP assessment is final</p>
                    </div>
                    <InfoTooltip content={LIQUIDATION_FINAL_TOOLTIP} />
                  </div>
                </button>
              </div>
            </div>

            {/* Liquidation Date */}
            <div>
              <label className="block text-xs font-bold text-white/60 uppercase tracking-wider mb-2">
                Liquidation Date
                <span className="text-white/30 normal-case font-normal ml-1">(date CBP liquidated the entry)</span>
              </label>
              <input
                type="date" value={liquidationDate} onChange={e => setLiquidationDate(e.target.value)}
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#FDB913] text-sm transition-colors"
              />
            </div>

            {/* Protest File Date */}
            <div>
              <label className="block text-xs font-bold text-white/60 uppercase tracking-wider mb-2">
                Protest File Date
                <span className="text-white/30 normal-case font-normal ml-1">(deadline to file protest with CBP)</span>
              </label>
              <input
                type="date" value={protestFileDate} onChange={e => setProtestFileDate(e.target.value)}
                className={`w-full px-4 py-3 bg-white/5 border rounded-lg text-white focus:outline-none text-sm transition-colors ${
                  protestWarning !== null
                    ? protestWarning <= 7 ? 'border-red-500/60 focus:border-red-500' : 'border-yellow-500/50 focus:border-yellow-500'
                    : 'border-white/10 focus:border-[#FDB913]'
                }`}
              />
              <p className="text-white/30 text-xs mt-1">
                Typically 90 days from the date of liquidation notice. Your attorney will confirm the exact deadline.
              </p>
            </div>

            {/* TOTAL ESTIMATED TARIFF REFUND — admin editable */}
            {adminMode && (
              <div className="bg-gradient-to-br from-green-500/10 to-green-500/5 border-2 border-green-500/30 rounded-xl p-4">
                <label className="block text-xs font-black text-green-400 uppercase tracking-wider mb-2">
                  <i className="ri-award-fill mr-1"></i>
                  Total Estimated Tariff Refund
                  <span className="text-white/30 normal-case font-normal ml-1">(attorney-confirmed amount shown to client)</span>
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-green-400/60 text-sm font-black">$</span>
                  <input
                    type="number" step="0.01" min="0"
                    value={totalEstimatedRefund} onChange={e => setTotalEstimatedRefund(e.target.value)}
                    className="w-full pl-8 pr-4 py-3 bg-green-500/10 border-2 border-green-500/30 rounded-lg text-green-300 placeholder-green-500/30 focus:outline-none focus:border-green-500/60 text-sm font-black transition-colors"
                    placeholder="0.00"
                  />
                </div>
                <p className="text-white/30 text-xs mt-1.5">
                  This amount will be displayed to the client in their portal as the official attorney-confirmed refund estimate.
                </p>
              </div>
            )}

            <button onClick={handleSave} disabled={saving}
              className={`w-full py-3 font-black text-sm rounded-xl transition-all whitespace-nowrap cursor-pointer disabled:opacity-50 ${
                saved ? 'bg-green-500/20 border-2 border-green-500/30 text-green-400'
                : 'bg-[#FDB913] hover:bg-[#e6a800] text-black'
              }`}>
              {saving ? <><i className="ri-loader-4-line animate-spin mr-2"></i>Saving...</>
                : saved ? <><i className="ri-checkbox-circle-fill mr-2"></i>Saved!</>
                : <><i className="ri-save-line mr-2"></i>Save Refund Information</>}
            </button>
          </div>
        )}

        {/* Document checklist */}
        <div className="bg-[#0d1520] border border-white/10 rounded-xl p-5">
          <p className="text-white/60 text-xs font-black uppercase tracking-wider mb-4">
            <i className="ri-file-list-3-line mr-1"></i>Required Documents for Refund Claim
          </p>
          <div className="space-y-3">
            {[
              {
                icon: 'ri-file-list-2-line', color: 'text-[#FDB913]',
                title: 'Entry Summary (CBP Form 7501)',
                desc: 'Shows duty assessed and paid. Must be uploaded by client through the portal.'
              },
              {
                icon: 'ri-bank-card-line', color: 'text-green-400',
                title: 'Proof of Payment',
                desc: 'Bank statements, electronic funds transfer records, or CBP receipt confirming cash payment.'
              },
              {
                icon: 'ri-file-text-line', color: 'text-sky-400',
                title: 'Import Invoices &amp; Packing Lists',
                desc: 'Demonstrate goods were imported under the affected IEEPA tariff code(s).'
              },
              {
                icon: 'ri-shield-check-line', color: 'text-orange-400',
                title: 'Customs Bond Documents',
                desc: 'If a bond was posted, include the bond number and amount.'
              },
            ].map((doc, i) => (
              <div key={i} className="flex items-start gap-3">
                <div className="w-8 h-8 flex items-center justify-center bg-white/5 rounded-lg flex-shrink-0">
                  <i className={`${doc.icon} ${doc.color} text-sm`}></i>
                </div>
                <div>
                  <p className="text-white text-xs font-bold" dangerouslySetInnerHTML={{ __html: doc.title }}></p>
                  <p className="text-white/40 text-xs mt-0.5 leading-relaxed">{doc.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {clientName && (
          <p className="text-white/20 text-xs text-center">
            Estimated refund data for {clientName} — subject to final legal determination
          </p>
        )}

        </> /* end adminMode || !eligibilityAuthorized */}
      </div>
    </div>
  );
}
